import type { DisplayStatusValue } from "../../types";

import classNames from "classnames";
import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { DisplayStatus, EventStatusEnum } from "../../types";

interface StatusStyleConfig {
  i18nKey: string;
  bgClass: string;
  textClass: string;
  tooltipKey?: string;
}

const STATUS_CONFIG: Record<string, StatusStyleConfig> = {
  [DisplayStatus.Approved]: {
    i18nKey: "events.statuses.published",
    bgClass: "bg-success-bg",
    textClass: "text-success",
  },
  [DisplayStatus.Moderation]: {
    i18nKey: "events.statuses.moderation",
    bgClass: "bg-warning-light",
    textClass: "text-yellow",
    tooltipKey: "events.statusTooltips.moderation",
  },
  [DisplayStatus.Paused]: {
    i18nKey: "events.statuses.paused",
    bgClass: "bg-lavender",
    textClass: "text-purple",
  },
  [DisplayStatus.Draft]: {
    i18nKey: "events.statuses.draft",
    bgClass: "bg-background-gray",
    textClass: "text-info",
  },
  [DisplayStatus.Finished]: {
    i18nKey: "events.statuses.finished",
    bgClass: "bg-sec-background-light",
    textClass: "text-blue",
  },
  [DisplayStatus.NeedsChanges]: {
    i18nKey: "events.statuses.rejected",
    bgClass: "bg-danger-pale",
    textClass: "text-danger",
  },
  [EventStatusEnum.Approved]: {
    i18nKey: "events.statuses.published",
    bgClass: "bg-success-bg",
    textClass: "text-success",
  },
  [EventStatusEnum.ModerationInProgress]: {
    i18nKey: "events.statuses.moderation",
    bgClass: "bg-warning-light",
    textClass: "text-yellow",
    tooltipKey: "events.statusTooltips.moderation",
  },
  [EventStatusEnum.Paused]: {
    i18nKey: "events.statuses.paused",
    bgClass: "bg-lavender",
    textClass: "text-purple",
  },
  [EventStatusEnum.Draft]: {
    i18nKey: "events.statuses.draft",
    bgClass: "bg-background-gray",
    textClass: "text-info",
  },
  [EventStatusEnum.Finished]: {
    i18nKey: "events.statuses.finished",
    bgClass: "bg-sec-background-light",
    textClass: "text-blue",
  },
  [EventStatusEnum.NeedsChanges]: {
    i18nKey: "events.statuses.rejected",
    bgClass: "bg-danger-pale",
    textClass: "text-danger",
  },
  [EventStatusEnum.Archived]: {
    i18nKey: "events.statuses.archived",
    bgClass: "bg-secondary",
    textClass: "text-info",
  },
};

interface EventStatusBadgeProps {
  status: DisplayStatusValue | EventStatusEnum;
  className?: string;
}

export default function EventStatusBadge({
  status,
  className,
}: EventStatusBadgeProps) {
  const { t } = useTranslation();
  const config =
    STATUS_CONFIG[status] ?? STATUS_CONFIG[EventStatusEnum.Archived];

  const badge = (
    <div
      className={classNames(
        "py-1 px-2 d-inline-flex rounded-3",
        config.bgClass,
        className
      )}
    >
      <span className={`${config.textClass} fs-7`}>{t(config.i18nKey)}</span>
    </div>
  );

  if (!config.tooltipKey) return badge;

  return (
    <OverlayTrigger
      placement="right"
      popperConfig={{
        modifiers: [
          {
            name: "flip",
            options: { fallbackPlacements: ["top", "bottom"] },
          },
        ],
      }}
      flip
      overlay={
        <Tooltip id={`tooltip-status-${status}`}>
          {t(config.tooltipKey)}
        </Tooltip>
      }
    >
      {badge}
    </OverlayTrigger>
  );
}
